import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = 'https://api.retro.kf-mau.ru';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Статистика
  const [stats, setStats] = useState({
    photos: 0,
    albums: 0,
    users: 0,
    tags: 0,
    comments: 0,
    pendingComments: 0,
  });

  const [pendingComments, setPendingComments] = useState([]);

  // Теги (модальное окно)
  const [allTags, setAllTags] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [newTagTitle, setNewTagTitle] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  // Пользователи
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Комментарии
  const [allComments, setAllComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentFilter, setCommentFilter] = useState('all');
  const [commentSearch, setCommentSearch] = useState('');

  // Модальные окна
  const [viewingComment, setViewingComment] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  const [editingUser, setEditingUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  const [deletingUser, setDeletingUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Защита маршрута
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'comments') {
      loadAllComments();
    }
  }, [user, navigate, activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [galleryRes, usersRes, tagsRes, commentsRes, commentsStatsRes] = await Promise.all([
        fetch(`${API}/api/gallery/`).then(r => r.json()),
        fetch(`${API}/users/`, { headers }).then(r => r.json()),
        fetch(`${API}/api/tags/`).then(r => r.json()),
        fetch(`${API}/api/comments/pending/`, { headers }).then(r => r.json()).catch(() => ({ comments: [] })),
        fetch(`${API}/api/comments/stats/`, { headers }).then(r => r.json()).catch(() => ({ total: 0, pending: 0 })),
      ]);

      const galleryData = galleryRes || [];
      const usersData = Array.isArray(usersRes) ? usersRes : [];
      const tagsData = Array.isArray(tagsRes) ? tagsRes : (tagsRes.tags || []);
      const pendingData = commentsRes.comments || [];
      const commentsStats = commentsStatsRes;

      const albumsSet = new Set(galleryData.filter(p => p.is_album).map(p => p.group_id));
      const photosCount = galleryData.length; //Все фото

      setStats({
        photos: photosCount,
        albums: albumsSet.size,
        users: usersData.length,
        tags: tagsData.length,
        comments: commentsStats.total || 0,
        pendingComments: commentsStats.pending || pendingData.length,
      });

      // Сохраняем все теги
      setAllTags(tagsData);
      setPendingComments(pendingData.slice(0, 5));
    } catch (err) {
      setError('Ошибка загрузки данных: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const usersWithAdminFlag = (Array.isArray(data) ? data : []).map(u => ({
        ...u,
        is_admin: Boolean(u.is_admin === 1 || u.is_admin === true)
      }));
      setUsers(usersWithAdminFlag);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAllComments = async () => {
    setCommentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/comments/all/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAllComments(data.comments || []);
    } catch (err) {
      console.error('Ошибка загрузки комментариев:', err);
      alert('Ошибка загрузки комментариев');
    } finally {
      setCommentsLoading(false);
    }
  };

  // Добавление нового тега
  const handleAddTag = async () => {
    if (!newTagTitle.trim()) return;

    setAddingTag(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API}/api/tags/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTagTitle.trim() })
      });

      if (res.ok) {
        const newTag = await res.json();
        setAllTags([...allTags, newTag]);
        setStats(prev => ({ ...prev, tags: prev.tags + 1 }));
        setNewTagTitle('');
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось добавить тег'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    } finally {
      setAddingTag(false);
    }
  };

  // Удаление тега
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Удалить этот тег?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/tags/${tagId}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setAllTags(allTags.filter(t => t.pk_tag !== tagId));
        setStats(prev => ({ ...prev, tags: prev.tags - 1 }));
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось удалить тег'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    }
  };

  const filteredComments = useMemo(() => {
    let filtered = allComments;
    if (commentFilter === 'approved') {
      filtered = filtered.filter(c => c.approved === 1);
    } else if (commentFilter === 'pending') {
      filtered = filtered.filter(c => c.approved === 0);
    }
    if (commentSearch.trim()) {
      const query = commentSearch.toLowerCase();
      filtered = filtered.filter(c =>
        c.description.toLowerCase().includes(query) ||
        c.username.toLowerCase().includes(query) ||
        (c.photo_title && c.photo_title.toLowerCase().includes(query)) ||
        (c.album_title && c.album_title.toLowerCase().includes(query))
      );
    }
    return filtered;
  }, [allComments, commentFilter, commentSearch]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(u =>
      u.username.toLowerCase().includes(query) ||
      u.email.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditUsername(u.username);
  };

  const handleSaveUser = async () => {
    if (!editingUser || !editUsername.trim()) return;
    setSavingUser(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/users/${editingUser.pk_user}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: editUsername.trim() })
      });
      if (res.ok) {
        await loadUsers();
        setEditingUser(null);
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось сохранить'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleAdmin = async (u) => {
    const action = u.is_admin ? 'снять права администратора' : 'назначить администратором';
    if (!window.confirm(`Вы уверены, что хотите ${action} для пользователя "${u.username}"?`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/users/${u.pk_user}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_admin: u.is_admin ? 0 : 1 })
      });
      if (res.ok) {
        await loadUsers();
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось изменить статус'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    setIsDeleting(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/users/${deletingUser.pk_user}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await loadUsers();
        setDeletingUser(null);
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось удалить'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModerateComment = async (commentId, approved) => {
    const actionText = approved ? 'одобрить' : 'отклонить и удалить';
    if (!window.confirm(`Вы уверены, что хотите ${actionText} этот комментарий?`)) return;

    const token = localStorage.getItem('token');
    try {
      if (approved) {
        const res = await fetch(`${API}/api/comment/${commentId}/moderate/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ approved: 1 })
        });

        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          throw new Error(error.error || 'Не удалось одобрить');
        }
      } else {
        const res = await fetch(`${API}/api/comment/${commentId}/delete/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          throw new Error(error.error || 'Не удалось отклонить');
        }
      }

      await loadDashboard();
      if (activeTab === 'comments') {
        await loadAllComments();
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот комментарий?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/comment/${commentId}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await loadAllComments();
        await loadDashboard();
      } else {
        const error = await res.json().catch(() => ({}));
        alert('Ошибка: ' + (error.error || 'Не удалось удалить'));
      }
    } catch (err) {
      alert('Ошибка сети: ' + err.message);
    }
  };

  const handleViewComment = async (comment) => {
    if (comment.photo_id) {
      setViewingComment(comment);
      setPhotoLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/gallery/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const allPhotos = await res.json();
        const photo = allPhotos.find(p => p.pk_photo === comment.photo_id);

        if (photo) {
          setPhotoData({
            url: photo.url,
            title: photo.title || photo.name_photo || 'Фотография',
            year: photo.year || '',
            group_id: comment.album_id || photo.group_id,
            hasAlbum: !!(comment.album_id || photo.group_id)
          });
        } else {
          alert('Фото не найдено');
          setViewingComment(null);
        }
      } catch (err) {
        alert('Ошибка: ' + err.message);
        setViewingComment(null);
      } finally {
        setPhotoLoading(false);
      }
    } else {
      alert('Фото не найдено');
    }
  };

  const closePhotoModal = () => {
    setViewingComment(null);
    setPhotoData(null);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader} />
        <p style={styles.loadingText}>Загрузка панели управления...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={{ color: '#e74c3c', fontSize: '1.1rem' }}>❌ {error}</p>
        <button onClick={loadDashboard} style={styles.retryBtn}>Повторить</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* === ШАПКА === */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Панель управления</h1>
          <p style={styles.subtitle}>Архив филиала МАУ • Администратор</p>
        </div>
        <button onClick={() => navigate('/')} style={styles.backBtn}>
          ← На сайт
        </button>
      </header>

      {/* === ВКЛАДКИ === */}
      <nav style={styles.tabs}>
        {[
          { id: 'dashboard', label: ' Общее' },
          { id: 'users', label: '👥 Пользователи' },
          { id: 'comments', label: '💬 Комментарии' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* === КОНТЕНТ === */}
      <main style={styles.main}>
        {activeTab === 'dashboard' && (
          <div>
            <div style={styles.statsGrid}>
              <StatCard value={stats.photos} label="Фотографий" color="#27ae60" />
              <StatCard value={stats.albums} label="Альбомов" color="#3498db" />

              {/* 🔥 Карточка тегов (открывает модальное окно) */}
              <div
                style={styles.tagsCard}
                onClick={() => setShowTagsModal(true)}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 35px rgba(155, 89, 182, 0.3)';
                  e.currentTarget.style.borderColor = '#9b59b6';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = '#9b59b640';
                }}
              >
                <div style={styles.statContent}>
                  <div style={{ ...styles.statValue, color: '#9b59b6' }}>{stats.tags}</div>
                  <div style={styles.statLabel}>
                    Тегов
                    <span style={styles.clickHint}>Нажмите →</span>
                  </div>
                </div>
                <div style={styles.clickArrow}>›</div>
              </div>

              <StatCard value={stats.users} label="Пользователей" color="#e67e22" />
              <StatCard value={stats.comments} label="Комментариев" color="#1abc9c" />
              <StatCard value={stats.pendingComments} label="На модерации" color="#f39c12" highlight={stats.pendingComments > 0} />
            </div>

            {pendingComments.length > 0 && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>⏳ Ожидают модерации ({pendingComments.length})</h2>
                <div style={styles.commentsList}>
                  {pendingComments.map(c => (
                    <div key={c.pk_comment} style={styles.commentCard}>
                      <div style={styles.commentHeader}>
                        <div style={styles.commentMeta}>
                          <strong style={styles.commentAuthor}>{c.username}</strong>
                          {c.photo_title && <span style={styles.commentPhoto}> {c.photo_title}</span>}
                        </div>
                        <span style={styles.commentDate}>{c.date_comment}</span>
                      </div>
                      <p style={styles.commentText}>{c.description}</p>
                      <div style={styles.commentActions}>
                        <button onClick={() => handleModerateComment(c.pk_comment, true)} style={styles.btnApprove}>✅ Одобрить</button>
                        <button onClick={() => handleModerateComment(c.pk_comment, false)} style={styles.btnReject}>❌ Отклонить</button>
                        <button onClick={() => handleViewComment(c)} style={styles.btnView}>👁 Просмотр</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingComments.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>✨</div>
                <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>Все комментарии одобрены! Новых заявок нет.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div style={styles.usersHeader}>
              <div>
                <h2 style={styles.sectionTitle}>👥 Пользователи ({users.length})</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                  Управление аккаунтами зарегистрированных пользователей
                </p>
              </div>
              <div style={styles.searchContainer}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Поиск по нику или email..."
                  style={styles.searchInput}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={styles.searchClear}>✕</button>
                )}
              </div>
            </div>

            {usersLoading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loader} />
                <p style={styles.loadingText}>Загрузка пользователей...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>{searchQuery ? '🔍' : '👥'}</div>
                <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>
                  {searchQuery ? 'Пользователи не найдены' : 'Нет зарегистрированных пользователей'}
                </p>
              </div>
            ) : (
              <div style={styles.usersTable}>
                <div style={styles.tableHeader}>
                  <div style={styles.colUser}>Пользователь</div>
                  <div style={styles.colEmail}>Email</div>
                  <div style={styles.colStatus}>Статус</div>
                  <div style={styles.colActions}>Действия</div>
                </div>
                {filteredUsers.map(u => {
                  const isCurrentUser = user?.pk_user === u.pk_user || user?.email === u.email;
                  return (
                    <div key={u.pk_user} style={styles.tableRow}>
                      <div style={styles.colUser}>
                        <div style={styles.userAvatar}>{u.username.charAt(0).toUpperCase()}</div>
                        <div>
                          <div style={styles.userName}>{u.username}</div>
                          {isCurrentUser && <span style={styles.currentUserBadge}>Это вы</span>}
                        </div>
                      </div>
                      <div style={styles.colEmail}><span style={styles.userEmail}>{u.email}</span></div>
                      <div style={styles.colStatus}>
                        {u.is_admin ? (
                          <span style={styles.badgeAdmin}>👑 Админ</span>
                        ) : (
                          <span style={styles.badgeUser}>👤 Пользователь</span>
                        )}
                      </div>
                      <div style={styles.colActions}>
                        <button onClick={() => openEditUser(u)} style={styles.btnAction} title="Редактировать имя">✏️</button>
                        {!isCurrentUser && (
                          <>
                            <button
                              onClick={() => handleToggleAdmin(u)}
                              style={{ ...styles.btnAction, color: u.is_admin ? '#e67e22' : '#27ae60' }}
                              title={u.is_admin ? 'Снять права админа' : 'Назначить админом'}
                            >
                              {u.is_admin ? '👑' : '🔑'}
                            </button>
                            <button
                              onClick={() => setDeletingUser(u)}
                              style={{ ...styles.btnAction, color: '#e74c3c' }}
                              title="Удалить аккаунт"
                            >
                              🗑
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div>
            <div style={styles.commentsHeader}>
              <div>
                <h2 style={styles.sectionTitle}>💬 Все комментарии ({allComments.length})</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                  Модерация и управление комментариями
                </p>
              </div>

              <div style={styles.commentsControls}>
                <div style={styles.filterGroup}>
                  <button
                    onClick={() => setCommentFilter('all')}
                    style={{
                      ...styles.filterBtn,
                      ...(commentFilter === 'all' ? styles.filterBtnActive : {})
                    }}
                  >
                    Все ({allComments.length})
                  </button>
                  <button
                    onClick={() => setCommentFilter('approved')}
                    style={{
                      ...styles.filterBtn,
                      ...(commentFilter === 'approved' ? styles.filterBtnApproved : {})
                    }}
                  >
                    ✅ Одобренные ({allComments.filter(c => c.approved === 1).length})
                  </button>
                  <button
                    onClick={() => setCommentFilter('pending')}
                    style={{
                      ...styles.filterBtn,
                      ...(commentFilter === 'pending' ? styles.filterBtnPending : {})
                    }}
                  >
                    ⏳ На модерации ({allComments.filter(c => c.approved === 0).length})
                  </button>
                </div>

                <div style={styles.searchContainer}>
                  <span style={styles.searchIcon}>🔍</span>
                  <input
                    type="text"
                    value={commentSearch}
                    onChange={e => setCommentSearch(e.target.value)}
                    placeholder="Поиск по тексту, автору, фото..."
                    style={styles.searchInput}
                  />
                  {commentSearch && (
                    <button onClick={() => setCommentSearch('')} style={styles.searchClear}>✕</button>
                  )}
                </div>
              </div>
            </div>

            {commentsLoading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loader} />
                <p style={styles.loadingText}>Загрузка комментариев...</p>
              </div>
            ) : filteredComments.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>
                  {commentSearch ? '🔍' : (commentFilter === 'pending' ? '✨' : '💬')}
                </div>
                <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>
                  {commentSearch ? 'Комментарии не найдены' :
                    commentFilter === 'pending' ? 'Нет комментариев на модерации' :
                      'Нет комментариев'}
                </p>
              </div>
            ) : (
              <div style={styles.commentsTable}>
                <div style={styles.commentTableHeader}>
                  <div style={styles.commentColAuthor}>Автор</div>
                  <div style={styles.commentColText}>Комментарий</div>
                  <div style={styles.commentColPhoto}>Фото/Альбом</div>
                  <div style={styles.commentColDate}>Дата</div>
                  <div style={styles.commentColStatus}>Статус</div>
                  <div style={styles.commentColActions}>Действия</div>
                </div>

                {filteredComments.map(c => (
                  <div
                    key={c.pk_comment}
                    style={{
                      ...styles.commentTableRow,
                      ...(c.approved === 0 ? styles.commentRowPending : {})
                    }}
                  >
                    <div style={styles.commentColAuthor}>
                      <div style={styles.userAvatar}>{c.username.charAt(0).toUpperCase()}</div>
                      <div>
                        <div style={styles.userName}>{c.username}</div>
                        <div style={styles.userEmail}>{c.user_email}</div>
                      </div>
                    </div>

                    <div style={styles.commentColText}>
                      <p style={styles.commentPreview}>{c.description}</p>
                    </div>

                    <div style={styles.commentColPhoto}>
                      {c.photo_title ? (
                        <div>
                          <div style={styles.photoTitle}> {c.photo_title}</div>
                          {c.album_title && (
                            <div style={styles.albumTitle}>📔 {c.album_title}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                      )}
                    </div>

                    <div style={styles.commentColDate}>
                      <span style={styles.commentDateText}>{c.date_comment}</span>
                    </div>

                    <div style={styles.commentColStatus}>
                      {c.approved === 1 ? (
                        <span style={styles.badgeApproved}>✅ Одобрен</span>
                      ) : (
                        <span style={styles.badgePending}>⏳ На модерации</span>
                      )}
                    </div>

                    <div style={styles.commentColActions}>
                      {c.photo_id && (
                        <button
                          onClick={() => handleViewComment(c)}
                          style={styles.btnAction}
                          title="Просмотреть фото"
                        >

                        </button>
                      )}
                      {c.approved === 0 && (
                        <>
                          <button
                            onClick={() => handleModerateComment(c.pk_comment, true)}
                            style={{ ...styles.btnAction, color: '#27ae60' }}
                            title="Одобрить"
                          >
                            ✅
                          </button>
                          <button
                            onClick={() => handleModerateComment(c.pk_comment, false)}
                            style={{ ...styles.btnAction, color: '#e74c3c' }}
                            title="Отклонить"
                          >
                            ❌
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteComment(c.pk_comment)}
                        style={{ ...styles.btnAction, color: '#e74c3c' }}
                        title="Удалить комментарий"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* МОДАЛЬНОЕ ОКНО ПРОСМОТРА ФОТО */}
      {viewingComment && (
        <div style={styles.modalOverlay} onClick={closePhotoModal}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            {photoLoading ? (
              <div style={styles.modalLoading}>
                <div style={styles.loader} />
                <p style={{ color: 'var(--text-muted)', marginTop: '15px' }}>Загрузка фото...</p>
              </div>
            ) : photoData ? (
              <>
                <div style={styles.modalHeader}>
                  <div>
                    <h3 style={styles.modalTitle}>{photoData.title}</h3>
                    <p style={styles.modalMeta}>{photoData.year} • Комментарий от {viewingComment.username}</p>
                  </div>
                  <button onClick={closePhotoModal} style={styles.closeBtn}>×</button>
                </div>
                <div style={styles.modalImageContainer}>
                  <img src={photoData.url} alt={photoData.title} style={styles.modalImage} />
                </div>
                <div style={styles.modalFooter}>
                  <div style={styles.modalCommentPreview}>
                    <strong style={{ color: 'var(--accent)' }}>Комментарий:</strong>
                    <p style={{ margin: '8px 0 0', color: 'var(--text-light)', lineHeight: 1.6 }}>
                      {viewingComment.description}
                    </p>
                  </div>

                  <div style={styles.modalNavigation}>
                    {viewingComment.photo_id && (
                      <button
                        onClick={() => {
                          if (viewingComment.album_id) {
                            window.open(`/#/album/${viewingComment.album_id}`, '_blank');
                          } else {
                            window.open(`/#/photo/${viewingComment.photo_id}`, '_blank');
                          }
                        }}
                        style={styles.btnNavigate}
                        title={viewingComment.album_id ? 'Открыть альбом с этим фото' : 'Открыть фото'}
                      >
                        {viewingComment.album_id ? '📔 Открыть альбом' : '📷 Открыть фото'}
                      </button>
                    )}
                  </div>

                  <div style={styles.modalActions}>
                    <button onClick={() => { closePhotoModal(); handleModerateComment(viewingComment.pk_comment, true); }} style={styles.btnApprove}>✅ Одобрить</button>
                    <button onClick={() => { closePhotoModal(); handleModerateComment(viewingComment.pk_comment, false); }} style={styles.btnReject}>❌ Отклонить</button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Ошибка загрузки фото</div>
            )}
          </div>
        </div>
      )}

      {/* 🔥 МОДАЛЬНОЕ ОКНО УПРАВЛЕНИЯ ТЕГАМИ */}
      {showTagsModal && (
        <div style={styles.modalOverlay} onClick={() => setShowTagsModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>🏷️ Управление тегами</h3>
                <p style={styles.modalMeta}>Всего тегов: {allTags.length}</p>
              </div>
              <button onClick={() => setShowTagsModal(false)} style={styles.closeBtn}>×</button>
            </div>

            <div style={{ padding: '25px 30px' }}>
              {/* Форма добавления тега */}
              <div style={styles.tagAddForm}>
                <input
                  type="text"
                  value={newTagTitle}
                  onChange={e => setNewTagTitle(e.target.value)}
                  placeholder="Название нового тега..."
                  style={styles.tagInput}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleAddTag()}
                />
                <button
                  onClick={handleAddTag}
                  disabled={addingTag || !newTagTitle.trim()}
                  style={{
                    ...styles.tagAddBtn,
                    opacity: addingTag || !newTagTitle.trim() ? 0.5 : 1,
                    cursor: addingTag || !newTagTitle.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {addingTag ? 'Добавление...' : '+ Добавить тег'}
                </button>
              </div>

              {/* Список тегов */}
              <div style={styles.tagsList}>
                {allTags.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>🏷️</div>
                    <p style={{ color: 'var(--text-muted)' }}>Нет тегов. Добавьте первый!</p>
                  </div>
                ) : (
                  allTags.map(tag => (
                    <div key={tag.pk_tag} style={styles.tagItem}>
                      <span style={styles.tagName}>
                        🏷️ {tag.title}
                      </span>
                      <button
                        onClick={() => handleDeleteTag(tag.pk_tag)}
                        style={styles.tagDeleteBtn}
                        title="Удалить тег"
                      >
                        🗑
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* МОДАЛЬНОЕ ОКНО РЕДАКТИРОВАНИЯ ПОЛЬЗОВАТЕЛЯ */}
      {editingUser && (
        <div style={styles.modalOverlay} onClick={() => setEditingUser(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>️ Редактировать пользователя</h3>
                <p style={styles.modalMeta}>{editingUser.email}</p>
              </div>
              <button onClick={() => setEditingUser(null)} style={styles.closeBtn}>×</button>
            </div>
            <div style={{ padding: '25px 30px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>Имя пользователя</label>
              <input
                type="text"
                value={editUsername}
                onChange={e => setEditUsername(e.target.value)}
                placeholder="Введите новое имя"
                style={styles.modalInput}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSaveUser()}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button onClick={() => setEditingUser(null)} style={styles.btnSecondary}>Отмена</button>
                <button onClick={handleSaveUser} disabled={savingUser || !editUsername.trim()} style={{ ...styles.btnPrimary, opacity: savingUser || !editUsername.trim() ? 0.5 : 1 }}>
                  {savingUser ? 'Сохранение...' : '💾 Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ ПОЛЬЗОВАТЕЛЯ */}
      {deletingUser && (
        <div style={styles.modalOverlay} onClick={() => !isDeleting && setDeletingUser(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '35px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⚠️</div>
              <h3 style={{ color: '#e74c3c', marginBottom: '10px', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem' }}>Удалить пользователя?</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '25px' }}>
                Вы уверены, что хотите удалить аккаунт <strong style={{ color: 'var(--text-light)' }}>"{deletingUser.username}"</strong>? Это действие необратимо.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => setDeletingUser(null)} disabled={isDeleting} style={styles.btnSecondary}>Отмена</button>
                <button onClick={handleDeleteUser} disabled={isDeleting} style={{ background: '#e74c3c', border: 'none', color: 'white', padding: '12px 24px', borderRadius: '8px', cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: 'Special Elite, monospace', fontSize: '0.9rem', opacity: isDeleting ? 0.6 : 1 }}>
                  {isDeleting ? 'Удаление...' : '🗑 Да, удалить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// КОМПОНЕНТ КАРТОЧКИ СТАТИСТИКИ
// ==========================================
const StatCard = ({ icon, value, label, color, highlight }) => (
  <div style={{
    ...styles.statCard,
    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
    border: `1px solid ${color}40`,
    ...(highlight ? styles.statCardHighlight : {}),
  }}>
    <div style={{ ...styles.statIconWrapper, background: `${color}20` }}>
      <span style={styles.statIcon}>{icon}</span>
    </div>
    <div style={styles.statContent}>
      <div style={{ ...styles.statValue, color: color }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  </div>
);

// ==========================================
// СТИЛИ
// ==========================================
const styles = {
  container: { minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '50px 40px', fontFamily: 'Inter, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '35px', borderBottom: '1px solid rgba(201, 169, 110, 0.2)', flexWrap: 'wrap', gap: '20px', position: 'relative', zIndex: 10 },
  h1: { fontSize: '2.2rem', fontWeight: 600, margin: 0, color: 'var(--accent)', fontFamily: 'Cormorant Garamond, serif' },
  subtitle: { color: 'var(--text-muted)', margin: '8px 0 0', fontSize: '0.95rem' },
  backBtn: { background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '10px 20px', cursor: 'pointer', borderRadius: '8px', fontFamily: 'Special Elite, monospace', fontSize: '0.9rem', transition: 'all 0.2s' },
  tabs: { display: 'flex', gap: '12px', marginBottom: '50px', flexWrap: 'wrap', position: 'relative', zIndex: 5 },
  tab: { background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', color: 'var(--text-muted)', padding: '12px 24px', cursor: 'pointer', fontSize: '0.95rem', borderRadius: '10px', transition: 'all 0.2s', fontFamily: 'Special Elite, monospace' },
  tabActive: { background: 'rgba(201, 169, 110, 0.15)', color: 'var(--accent)', border: '1px solid rgba(201, 169, 110, 0.4)', fontWeight: 600 },
  main: { minHeight: '400px' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' },
  statCard: { borderRadius: '16px', padding: '28px 24px', display: 'flex', alignItems: 'center', gap: '18px', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' },
  statCardHighlight: { animation: 'pulse 2s infinite', boxShadow: '0 8px 30px rgba(243, 156, 18, 0.2)' },
  statContent: { flex: 1, minWidth: 0 },
  statValue: { fontSize: '2.2rem', fontWeight: 800, lineHeight: 1, marginBottom: '4px', letterSpacing: '-0.5px', textShadow: '0 2px 8px rgba(0,0,0,0.2)' },
  statLabel: { fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 500, letterSpacing: '0.3px' },

  section: { marginBottom: '40px' },
  sectionTitle: { fontSize: '1.3rem', color: 'var(--text-light)', marginBottom: '20px', fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 },

  commentsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  commentCard: { background: 'rgba(243, 156, 18, 0.05)', border: '1px solid rgba(243, 156, 18, 0.2)', borderLeft: '3px solid #f39c12', borderRadius: '10px', padding: '20px' },
  commentHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' },
  commentMeta: { display: 'flex', flexDirection: 'column', gap: '5px' },
  commentAuthor: { color: 'var(--accent)', fontFamily: 'Special Elite, monospace', fontSize: '1rem' },
  commentPhoto: { color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' },
  commentDate: { color: 'var(--text-muted)', fontSize: '0.85rem' },
  commentText: { color: 'var(--text-light)', lineHeight: 1.6, margin: '10px 0 15px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' },
  commentActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  btnApprove: { background: 'rgba(39, 174, 96, 0.15)', border: '1px solid rgba(39, 174, 96, 0.4)', color: '#27ae60', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s' },
  btnReject: { background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.4)', color: '#e74c3c', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s' },
  btnView: { background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', color: 'var(--accent)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s' },

  emptyState: { textAlign: 'center', padding: '60px 20px', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px', border: '1px dashed rgba(255, 255, 255, 0.1)' },

  quickActions: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' },
  quickBtn: { background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: 'var(--text-light)', fontSize: '0.9rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s' },

  loadingContainer: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-dark)', gap: '20px' },
  loader: { width: '40px', height: '40px', border: '3px solid rgba(201, 169, 110, 0.2)', borderTop: '3px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { color: 'var(--text-muted)', fontSize: '1rem' },
  errorContainer: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-dark)', gap: '20px' },
  retryBtn: { background: 'var(--accent)', border: 'none', color: 'var(--bg-dark)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Special Elite, monospace' },

  // Пользователи
  usersHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' },
  searchContainer: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: '14px', fontSize: '1rem', opacity: 0.6 },
  searchInput: { width: '320px', padding: '12px 40px 12px 42px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '10px', color: 'var(--text-light)', fontSize: '0.95rem', fontFamily: 'inherit', outline: 'none', transition: 'all 0.2s' },
  searchClear: { position: 'absolute', right: '10px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '5px' },

  usersTable: { background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', overflow: 'hidden' },
  tableHeader: { display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', padding: '16px 24px', background: 'rgba(201, 169, 110, 0.08)', borderBottom: '1px solid rgba(201, 169, 110, 0.2)', fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', color: 'var(--text-muted)', letterSpacing: '1px' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', padding: '16px 24px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', alignItems: 'center', transition: 'background 0.2s' },
  colUser: { display: 'flex', alignItems: 'center', gap: '12px' },
  colEmail: { color: 'var(--text-muted)', fontSize: '0.9rem' },
  colStatus: {},
  colActions: { display: 'flex', gap: '8px' },

  userAvatar: { width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.3), rgba(201, 169, 110, 0.1))', border: '1px solid rgba(201, 169, 110, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', color: 'var(--accent)', fontFamily: 'Special Elite, monospace' },
  userName: { color: 'var(--text-light)', fontWeight: 500, fontSize: '0.95rem' },
  userEmail: { color: 'var(--text-muted)', fontSize: '0.85rem' },
  currentUserBadge: { fontSize: '0.7rem', color: 'var(--accent)', fontStyle: 'italic' },

  badgeAdmin: { background: 'rgba(243, 156, 18, 0.15)', color: '#f39c12', padding: '6px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, border: '1px solid rgba(243, 156, 18, 0.3)' },
  badgeUser: { background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-muted)', padding: '6px 12px', borderRadius: '20px', fontSize: '0.8rem', border: '1px solid rgba(255, 255, 255, 0.1)' },

  btnAction: { background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: 'var(--text-light)', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' },

  // Комментарии
  commentsHeader: { marginBottom: '30px' },
  commentsControls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap', gap: '20px' },
  filterGroup: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  filterBtn: { background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', color: 'var(--text-muted)', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s' },
  filterBtnActive: { background: 'rgba(201, 169, 110, 0.15)', color: 'var(--accent)', border: '1px solid rgba(201, 169, 110, 0.4)' },
  filterBtnApproved: { background: 'rgba(39, 174, 96, 0.15)', color: '#27ae60', border: '1px solid rgba(39, 174, 96, 0.4)' },
  filterBtnPending: { background: 'rgba(243, 156, 18, 0.15)', color: '#f39c12', border: '1px solid rgba(243, 156, 18, 0.4)' },

  commentsTable: { background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', overflow: 'hidden' },
  commentTableHeader: { display: 'grid', gridTemplateColumns: '1.5fr 2.5fr 1.5fr 1fr 1fr 1fr', padding: '16px 20px', background: 'rgba(201, 169, 110, 0.08)', borderBottom: '1px solid rgba(201, 169, 110, 0.2)', fontFamily: 'Special Elite, monospace', fontSize: '0.8rem', color: 'var(--text-muted)', letterSpacing: '1px' },
  commentTableRow: { display: 'grid', gridTemplateColumns: '1.5fr 2.5fr 1.5fr 1fr 1fr 1fr', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', alignItems: 'start', gap: '10px', transition: 'background 0.2s' },
  commentRowPending: { background: 'rgba(243, 156, 18, 0.03)' },

  commentColAuthor: { display: 'flex', alignItems: 'center', gap: '10px' },
  commentColText: { maxWidth: '400px', overflow: 'hidden' },
  commentColPhoto: {},
  commentColDate: {},
  commentColStatus: {},
  commentColActions: { display: 'flex', gap: '6px', justifyContent: 'flex-end' },

  tagsCard: {
    borderRadius: '16px',
    padding: '28px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    background: `linear-gradient(135deg, #9b59b615 0%, #9b59b605 100%)`,
    border: `1px solid #9b59b640`,
    cursor: 'pointer',
  },
  clickHint: {
    fontSize: '0.7rem',
    color: '#9b59b6',
    marginLeft: '8px',
    opacity: 0.7,
    fontStyle: 'italic'
  },
  clickArrow: {
    fontSize: '1.5rem',
    color: '#9b59b6',
    opacity: 0.5,
    marginLeft: 'auto',
    transition: 'all 0.3s ease'
  },

  commentPreview: { color: 'var(--text-light)', fontSize: '0.9rem', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', maxHeight: '120px', overflow: 'auto', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' },
  photoTitle: { color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '4px' },
  albumTitle: { color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' },
  commentDateText: { color: 'var(--text-muted)', fontSize: '0.85rem' },

  badgeApproved: { background: 'rgba(39, 174, 96, 0.15)', color: '#27ae60', padding: '5px 10px', borderRadius: '15px', fontSize: '0.75rem', fontWeight: 600, border: '1px solid rgba(39, 174, 96, 0.3)' },
  badgePending: { background: 'rgba(243, 156, 18, 0.15)', color: '#f39c12', padding: '5px 10px', borderRadius: '15px', fontSize: '0.75rem', fontWeight: 600, border: '1px solid rgba(243, 156, 18, 0.3)' },

  // Модальные окна
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(5, 5, 3, 0.95)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', backdropFilter: 'blur(5px)' },
  modalContent: { background: 'linear-gradient(160deg, #1c1c16 0%, #12120e 100%)', border: '1.5px solid rgba(201, 169, 110, 0.6)', borderRadius: '16px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 80px rgba(0, 0, 0, 0.7)' },
  modalLoading: { padding: '60px 40px', textAlign: 'center' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '25px 30px', borderBottom: '1px solid rgba(201, 169, 110, 0.2)', gap: '20px' },
  modalTitle: { margin: '0 0 5px 0', fontSize: '1.5rem', color: 'var(--text-light)', fontFamily: 'Cormorant Garamond, serif' },
  modalMeta: { margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' },
  closeBtn: { background: 'rgba(255, 255, 255, 0.1)', border: 'none', color: 'var(--text-light)', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', lineHeight: 1 },
  modalImageContainer: { padding: '30px', background: 'rgba(0, 0, 0, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalImage: { maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)' },
  modalFooter: { padding: '25px 30px', borderTop: '1px solid rgba(201, 169, 110, 0.2)' },
  modalCommentPreview: { marginBottom: '20px', padding: '15px', background: 'rgba(243, 156, 18, 0.05)', borderRadius: '8px', borderLeft: '3px solid #f39c12' },
  modalNavigation: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  btnNavigate: { background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.4)', color: 'var(--accent)', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Special Elite, monospace', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' },
  modalActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  modalInput: { width: '100%', padding: '12px 15px', background: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '8px', color: 'var(--text-light)', fontSize: '1rem', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
  btnSecondary: { background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.2)', color: 'var(--text-muted)', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Special Elite, monospace', fontSize: '0.9rem' },
  btnPrimary: { background: 'var(--accent)', border: 'none', color: 'var(--bg-dark)', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Special Elite, monospace', fontSize: '0.9rem', fontWeight: 600 },

  // Теги (модальное окно)
  tagAddForm: {
    display: 'flex',
    gap: '12px',
    marginBottom: '25px'
  },
  tagInput: {
    flex: 1,
    padding: '12px 15px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(155, 89, 182, 0.3)',
    borderRadius: '8px',
    color: 'var(--text-light)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'all 0.2s'
  },
  tagAddBtn: {
    background: '#9b59b6',
    border: 'none',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    fontFamily: 'Special Elite, monospace',
    fontSize: '0.9rem',
    fontWeight: 600,
    transition: 'all 0.2s'
  },
  tagsList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '12px',
    maxHeight: '400px',
    overflowY: 'auto',
    paddingRight: '10px'
  },
  tagItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    background: 'rgba(155, 89, 182, 0.1)',
    border: '1px solid rgba(155, 89, 182, 0.2)',
    borderRadius: '10px',
    transition: 'all 0.2s'
  },
  tagName: {
    color: 'var(--text-light)',
    fontSize: '0.95rem',
    fontWeight: 500
  },
  tagDeleteBtn: {
    background: 'rgba(231, 76, 60, 0.15)',
    border: '1px solid rgba(231, 76, 60, 0.3)',
    color: '#e74c3c',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    50% { 
      transform: scale(1.02);
      box-shadow: 0 8px 30px rgba(243, 156, 18, 0.25);
    }
  }
`;
document.head.appendChild(styleSheet);